import { Send, ShieldCheck, Factory, FileSearch, CheckCircle2, Newspaper, ChevronRight, RotateCcw, BadgeCheck } from "lucide-react";

const ACTORS = {
  requestor: { label: "Requestor", cls: "bg-primary/10 text-primary" },
  leadership: { label: "Leadership", cls: "bg-accent/15 text-accent" },
  comms: { label: "Communications", cls: "bg-secondary text-foreground/80" },
};

const STAGES = [
  { icon: Send, actor: "requestor", title: "Submit a request", body: "A Communications Request or Sunday News item is submitted (draft first, if you like)." },
  { icon: ShieldCheck, actor: "leadership", title: "Leadership 1st approval", body: "President & VP give the go-ahead to produce — Approve, Approve with edits, or Deny." },
  { icon: Factory, actor: "comms", title: "Production", body: "Communications assigns a teammate and creates the piece through production stages." },
  { icon: FileSearch, actor: "comms", title: "Proof sent", body: "A proof is shared with the requestor for review before anything is finalized." },
  { icon: CheckCircle2, actor: "requestor", title: "Requestor approves", body: "The requestor approves the proof — or requests changes, sending it back to Production." },
  { icon: BadgeCheck, actor: "leadership", title: "Leadership final approval", body: "President & VP give final sign-off before anything goes out." },
  { icon: Newspaper, actor: "comms", title: "Publishing / delivery", body: "The item is published or the final files delivered. Everyone is emailed at each step." },
];

export const ProcessFlow = () => {
  return (
    <div data-testid="process-flow">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-stretch">
        {STAGES.map((s, i) => {
          const actor = ACTORS[s.actor];
          const loops = i === 4; // "requestor approves" can loop back to production
          return (
            <div key={i} className="flex flex-col md:flex-row md:items-stretch">
              <div
                data-testid={`flow-stage-${i}`}
                className="relative flex w-full flex-col rounded-2xl border border-border bg-card p-4 transition-transform hover:-translate-y-1 hover:shadow-md md:w-[210px]"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${actor.cls}`}>
                    {actor.label}
                  </span>
                </div>
                <div className="mt-3 flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <s.icon className="h-5 w-5" />
                </div>
                <p className="mt-3 font-heading text-sm font-bold leading-tight">{s.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{s.body}</p>
                {loops && (
                  <span className="mt-3 inline-flex items-center gap-1 self-start rounded-full border border-dashed border-accent/50 bg-accent/5 px-2 py-1 text-[10px] font-medium text-accent">
                    <RotateCcw className="h-3 w-3" /> Changes → back to step 3
                  </span>
                )}
              </div>
              {i < STAGES.length - 1 && (
                <div className="flex items-center justify-center py-1 text-muted-foreground/50 md:px-1 md:py-0">
                  <ChevronRight className="h-5 w-5 rotate-90 md:rotate-0" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground" data-testid="flow-legend">
        <span className="font-medium text-foreground/70">Who acts:</span>
        {Object.values(ACTORS).map((a) => (
          <span key={a.label} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${a.cls.split(" ")[0]}`} />
            {a.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <RotateCcw className="h-3.5 w-3.5 text-accent" /> Loops back if changes are requested
        </span>
      </div>
    </div>
  );
};
